import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { CharxSdk } from '../core/sdk.js'

const CharxContext = createContext<CharxSdk | null>(null)

interface CharxProviderProps {
  children: ReactNode
  /** Rendered while the SDK handshake is in progress */
  fallback?: ReactNode
  /** Rendered if SDK init fails (e.g. not inside the platform iframe) */
  errorFallback?: (error: Error) => ReactNode
}

export function CharxProvider({ children, fallback, errorFallback }: CharxProviderProps) {
  const [sdk, setSdk] = useState<CharxSdk | null>(null)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const instance = CharxSdk.getInstance()
    instance.init()
      .then(() => setSdk(instance))
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
  }, [])

  if (error) return <>{errorFallback?.(error) ?? null}</>
  if (!sdk) return <>{fallback ?? null}</>
  return <CharxContext.Provider value={sdk}>{children}</CharxContext.Provider>
}

export function useCharx(): CharxSdk {
  const sdk = useContext(CharxContext)
  if (!sdk) throw new Error('useCharx must be used inside <CharxProvider>')
  return sdk
}
